/**
 * Generalized File Download Utilities
 * 
 * This utility provides consistent file download functionality across the application
 * following the established patterns in the codebase.
 */

export function extractFilenameFromContentDisposition(response: Response, fallback: string): string {
  const contentDisposition = response.headers.get('Content-Disposition');
  if (!contentDisposition) {
    return fallback;
  }
  const match = contentDisposition.match(/filename="?(.+)"?/);
  return match ? match[1] : fallback;
}

/**
 * Download a file from a URL
 * @param url - The URL of the file to download
 * @param filename - Optional custom filename for the downloaded file
 * @param onSuccess - Optional success callback
 * @param onError - Optional error callback
 */
const downloadFile = (
  url: string, 
  filename?: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void,
) => {
  try {
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    
    if (filename) {
      link.download = filename;
    }
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    onSuccess?.();
  } catch (error) {
    const downloadError = error instanceof Error ? error : new Error('Download failed');
    onError?.(downloadError);
    console.error('Error downloading file:', downloadError);
  }
};

/**
 * Download a file from the public directory
 * @param filepath - Path relative to the public directory (e.g., 'downloads/file.pdf')
 * @param filename - Optional custom filename for the downloaded file
 * @param onSuccess - Optional success callback
 * @param onError - Optional error callback
 */
export const downloadPublicFile = (
  filepath: string,
  filename?: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void,
) => {
  const url = `/${filepath}`;
  downloadFile(url, filename, onSuccess, onError);
};

export const downloadCommonFiles = {
  contractSousTraitant: (customFilename?: string) => 
    downloadPublicFile(
      'downloads/contrat-sous-traitant.docx', 
      customFilename || 'contrat-sous-traitant.docx',
    ),
    
  demoFile: (customFilename?: string) => 
    downloadPublicFile(
      'downloads/demo.pdf', 
      customFilename || 'demo.pdf',
    ),

  boitageV1: (customFilename?: string) =>
    downloadPublicFile(
      'downloads/v1-MODELE-BOITAGE-IMMEUBLE.docx',
      customFilename || 'v1-MODELE-BOITAGE-IMMEUBLE.docx',
    ),

  boitageV2: (customFilename?: string) =>
    downloadPublicFile(
      'downloads/v2-MODELE-BOITAGE-IMMEUBLE.docx',
      customFilename || 'v2-MODELE-BOITAGE-IMMEUBLE.docx',
    ),

  boitageV3: (customFilename?: string) =>
    downloadPublicFile(
      'downloads/v3-MODELE-BOITAGE-IMMEUBLE.docx',
      customFilename || 'v3-MODELE-BOITAGE-IMMEUBLE.docx',
    ),
};
